import { useMemo } from "react";
import { useMonyawn } from "../../../context/MonyawnContext";
import { getGlobalOpsSnapshot } from "../../../lib/ops";

export function GlobalOpsHero() {
  const { state, patchState } = useMonyawn();

  const snapshot = useMemo(() => getGlobalOpsSnapshot(state), [state]);
  const topSignals = snapshot.signals.slice(0, 3);

  if (snapshot.total_active_opportunities === 0) return null;

  return (
    <div className="toy-surface rounded-[2.5rem] border border-black/10 bg-brand-highlight/10 shadow-brand-shadow p-6 lg:p-8 mb-10 overflow-hidden relative">
      {/* Background decoration */}
      <div className="absolute -top-20 -right-20 w-64 h-64 bg-brand-accent/5 rounded-full blur-3xl pointer-events-none"></div>

      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 relative z-10">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="flex h-2 w-2 rounded-full bg-brand-accent animate-pulse"></span>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-accent">
              Global Command Tower
            </p>
          </div>
          <h2 className="text-2xl lg:text-3xl font-extrabold text-brand-ink tracking-tight">
            You have {snapshot.critical_incidents} critical incidents 🛰️
          </h2>
          <p className="text-sm text-brand-muted mt-1">
            Monitoring {snapshot.total_active_opportunities} products. Sequencing priority based on
            leverage risk.
          </p>
        </div>

        <div className="flex flex-wrap gap-4">
          <div className="bg-white/60 border border-black/5 px-4 py-3 rounded-2xl">
            <p className="text-[10px] font-bold uppercase tracking-widest text-brand-muted">
              Active Plays
            </p>
            <p className="text-xl font-bold text-brand-ink">
              {snapshot.total_active_opportunities}
            </p>
          </div>
          <div className="bg-white/60 border border-black/5 px-4 py-3 rounded-2xl">
            <p className="text-[10px] font-bold uppercase tracking-widest text-brand-muted">
              Open Escalations
            </p>
            <p className="text-xl font-bold text-brand-ink">{snapshot.total_open_escalations}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
        {topSignals.map((signal) => (
          <button
            key={signal.opportunity_id}
            onClick={() =>
              patchState(
                (s) => ({ ...s, selectedOpportunityId: signal.opportunity_id }),
                "Switched via Command Tower",
              )
            }
            className={`text-left p-4 rounded-[1.5rem] border transition-all hover:scale-[1.02] active:scale-[0.98] ${
              signal.urgency_score > 80
                ? "bg-red-50 border-red-200"
                : signal.urgency_score > 50
                  ? "bg-orange-50 border-orange-200"
                  : "bg-white/40 border-black/5"
            }`}
          >
            <div className="flex justify-between items-start mb-2">
              <span
                className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ${
                  signal.priority === "critical"
                    ? "bg-red-500 text-white"
                    : "bg-black/5 text-brand-muted"
                }`}
              >
                {signal.priority}
              </span>
              <span className="text-[10px] font-mono font-bold text-brand-muted">
                Score: {signal.urgency_score}
              </span>
            </div>
            <p className="font-bold text-brand-ink truncate">{signal.company_name}</p>
            <p className="text-xs text-brand-muted truncate">{signal.role_title}</p>

            <div className="flex gap-3 mt-3">
              {signal.open_escalations > 0 && (
                <span className="text-[10px] font-bold text-red-600">
                  🚨 {signal.open_escalations} Alerts
                </span>
              )}
              {signal.blocked_tasks > 0 && (
                <span className="text-[10px] font-bold text-orange-600">
                  🚧 {signal.blocked_tasks} Blocked
                </span>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
